import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { Notify, toast } from "@/components/ui/notify";
import { useBatchCharacteristics } from "@/features/organization/batch-characteristics/hooks";
import type { BatchResponse } from "@/types/batches.types";
import { CharacteristicValueField } from "./CharacteristicValueField";
import { effectiveLabel, provenanceLabel } from "./characteristic-labels";
import { originsLabel, originStatusLabel } from "./origin-labels";
import { useBatch, useSetBatchCharacteristics, useSetBatchCode } from "./hooks";
import { useBatchEditDraft } from "./useBatchEditDraft";

interface BatchEditDialogProps {
  lineId: string;
  runId: string;
  onClose: () => void;
}

/** Spec 084 — corrigir a batelada. A regra de papel e de herança vem pronta da API (`editable`). */
export function BatchEditDialog({ lineId, runId, onClose }: BatchEditDialogProps) {
  const query = useBatch(lineId, runId);
  return (
    <ModalOverlay onClose={onClose} title="Corrigir batelada" className="max-w-lg">
      <CardHeader>
        <CardTitle>Corrigir batelada</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isError ? (
          <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data ? (
          <BatchEditForm lineId={lineId} batch={query.data} onClose={onClose} />
        ) : (
          <p className="text-[13px] text-text-secondary">Carregando a batelada…</p>
        )}
      </CardContent>
    </ModalOverlay>
  );
}

function BatchEditForm({
  lineId,
  batch,
  onClose,
}: {
  lineId: string;
  batch: BatchResponse;
  onClose: () => void;
}) {
  const defsQuery = useBatchCharacteristics();
  const draft = useBatchEditDraft(batch);
  const setValues = useSetBatchCharacteristics(lineId, batch.runId);
  const setCode = useSetBatchCode(lineId, batch.runId);
  const options = new Map((defsQuery.data ?? []).map((d) => [d.id, d.values]));
  const readOnly = batch.characteristics.filter((c) => !c.editable);
  const pending = setValues.isPending || setCode.isPending;
  const nothing = draft.changes.length === 0 && !draft.codeChanged;

  async function salvar() {
    // Erros sobem para o toast global (MutationCache); aqui só fecha no sucesso.
    if (draft.codeChanged) await setCode.mutateAsync(draft.code.trim());
    if (draft.changes.length > 0) await setValues.mutateAsync({ values: draft.changes });
    toast.success("Batelada corrigida.");
    onClose();
  }

  return (
    <div className="space-y-4">
      {batch.canEditBatchCode ? (
        <div className="space-y-1.5">
          <Label htmlFor="batch-code">Código da batelada</Label>
          <Input
            id="batch-code"
            value={draft.code}
            maxLength={40}
            onChange={(e) => draft.setCode(e.target.value)}
          />
        </div>
      ) : (
        <p className="text-[13px] text-text-secondary">
          Batelada <span className="font-mono text-text-primary">{batch.batchCode}</span>
        </p>
      )}
      {batch.originStatus !== null ? (
        <p className="text-[12px] text-text-secondary">
          {originStatusLabel(batch.originStatus)} · origem:{" "}
          <span className="text-text-primary">{originsLabel(batch.origins)}</span>
        </p>
      ) : null}
      {defsQuery.isError ? (
        <Notify.QueryError error={defsQuery.error} onRetry={() => void defsQuery.refetch()} />
      ) : null}
      {draft.editable.map((c) => (
        <CharacteristicValueField
          key={c.characteristicId}
          idPrefix="batch-edit"
          def={{
            id: c.characteristicId,
            name: c.name,
            kind: c.kind,
            required: c.required,
            options: options.get(c.characteristicId) ?? [],
          }}
          value={draft.values.get(c.characteristicId)}
          onChange={(v) => draft.set(c.characteristicId, v)}
          disabled={pending}
        />
      ))}
      {readOnly.length > 0 ? (
        <div className="space-y-1 rounded-md bg-bg-muted-rd px-3 py-2 text-[12px]">
          <p className="text-text-secondary">Não se corrige aqui:</p>
          {readOnly.map((c) => (
            <p key={c.characteristicId} className="text-text-primary">
              {c.name}: {effectiveLabel(c)}
              <span className="text-text-tertiary">
                {" "}
                · {provenanceLabel(c) ?? "sem permissão"}
                {c.state === "INHERITED" || c.state === "MIXED" ? " — corrija na origem" : ""}
              </span>
            </p>
          ))}
        </div>
      ) : null}
      {draft.missing.length > 0 ? (
        <p role="status" className="text-[12px] text-rd-warning-700">
          Falta informar: {draft.missing.join(", ")}.
        </p>
      ) : null}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={() => void salvar().catch(() => undefined)} disabled={pending || nothing}>
          {pending ? "Salvando…" : "Salvar"}
        </Button>
      </div>
    </div>
  );
}
